import { Box, styled } from '@mui/material';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { FC, useMemo } from 'react';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';

interface IRiskBadgeProps {
  size?: 'small' | 'medium';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'PENDING';
  text?: string;
  isCrossedOut?: boolean;
}

const Container = styled(Box)<{
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'PENDING';
  size: 'small' | 'medium';
}>(({ theme, riskLevel, size }) => ({
  display: 'flex',
  width: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  padding: theme.spacing(size === 'small' ? 0.2 : 1, 1),
  backgroundColor:
    riskLevel === 'HIGH'
      ? theme.custom.themeColors.error[20]
      : riskLevel === 'MEDIUM'
      ? theme.custom.themeColors.accent[20]
      : theme.custom.themeColors.success[20],
}));

const RiskBadgeText = styled(ThemeTypography)<{
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'PENDING';
  'is-crossed-out'?: Tbooleanish;
}>(({ theme, riskLevel, 'is-crossed-out': isCrossedOut }) => ({
  color: `${
    riskLevel === 'HIGH'
      ? theme.custom.themeColors.error[100]
      : riskLevel === 'MEDIUM'
      ? theme.custom.themeColors.accent[100]
      : theme.custom.themeColors.success[100]
  } !important`,
  textDecoration: isCrossedOut === 'true' ? 'line-through' : 'none',
}));

const RiskLevelBadge: FC<IRiskBadgeProps> = ({ riskLevel, text, isCrossedOut, size = 'small' }) => {
  const _text = useMemo(() => {
    if (text) {
      return text;
    }
    switch (riskLevel) {
      case 'HIGH':
        return 'High risk';
      case 'MEDIUM':
        return 'Medium risk';
      case 'LOW':
        return 'Low risk';
      case 'PENDING':
        return 'Pending';
    }
  }, [riskLevel, text]);

  return (
    <Container riskLevel={riskLevel} size={size}>
      <RiskBadgeText
        variant="BODY_SMALL_BOLD"
        riskLevel={riskLevel}
        is-crossed-out={Booleanish(isCrossedOut)}
        autoOverflow
      >
        {_text}
      </RiskBadgeText>
    </Container>
  );
};

export default RiskLevelBadge;
